import { useRef, useState } from 'react'
import type ReactECharts from 'echarts-for-react'
import { Download } from 'lucide-react'
import { Button } from '@src/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import RiskProfileAssetRiskQuadrantChart from '../../../chart/RiskProfileAssetRiskQuadrant.chart'
import { useProfileAllAssetsData } from '../../../hooks/useProfileAssetsRequest'
import { useRiskProfileRequest } from '../../../hooks/useRiskProfileRequest'
import { useExportChartImage } from '../../../hooks/useExportChartImage'
import { useExportAssetsCsv } from '../../../hooks/useExportAssetsCsv'
import type { RiskProfileQueryParams } from '../../../hooks/useRiskProfileQueryParams'
import { ChartQueryState } from '../../ChartQueryState'
import { InterpretPanel, InterpretToggle } from '../../InterpretToggle'
import { RiskProfileCard } from '../../RiskProfileCard'

export interface RiskProfileAssetRiskQuadrantWidgetProps {
  queryParams: RiskProfileQueryParams | null
}

export function RiskProfileAssetRiskQuadrantWidget({
  queryParams,
}: RiskProfileAssetRiskQuadrantWidgetProps) {
  const [showInterpret, setShowInterpret] = useState(false)
  const assetsQuery = useProfileAllAssetsData(queryParams)
  const exportRequest = useRiskProfileRequest({ queryParams, widget: 'riskByElementType' })
  const chartRef = useRef<ReactECharts>(null)
  const handleExportImage = useExportChartImage(chartRef, 'asset-risk-quadrant')
  const { exportCsv, isExporting } = useExportAssetsCsv(exportRequest)

  return (
    <RiskProfileCard
      title="Asset risk quadrant"
      description="Scatter of replacement cost against damage cost. Quadrants split at the median of each axis."
      onExport={
        <div className="flex items-center gap-2">
          <InterpretToggle active={showInterpret} onClick={() => setShowInterpret((v) => !v)} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0 gap-1.5 px-2 text-xs"
              >
                Export
                <Download className="size-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportImage}>Export as Image</DropdownMenuItem>
              <DropdownMenuItem onClick={exportCsv} disabled={isExporting || !exportRequest}>
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
      className="min-h-96"
    >
      {showInterpret && (
        <InterpretPanel>
          <p>
            This figure compares element value and damage severity. Elements in the upper-right
            combine high value and high damage, driving higher loss exposure. Upper-left elements
            may indicate operational fragility, while lower-right elements represent high-value
            elements with lower relative damage.
          </p>
        </InterpretPanel>
      )}
      <ChartQueryState query={assetsQuery}>
        {(data) => <RiskProfileAssetRiskQuadrantChart ref={chartRef} data={data} />}
      </ChartQueryState>
    </RiskProfileCard>
  )
}
